import { useEffect, useState } from "react";
import api, { fmtDate, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Globe, RefreshCw, Trash2, ExternalLink, Lock, Send } from "lucide-react";

const STATO = {
  non_pubblicato: { label: "Non pubblicato", cls: "text-[var(--muted)] bg-slate-100 border-[var(--border)]" },
  in_sincronizzazione: { label: "In sincronizzazione", cls: "text-blue-600 bg-blue-500/10 border-blue-400/30" },
  pubblicato: { label: "Pubblicato", cls: "text-emerald-600 bg-emerald-400/10 border-emerald-400/30" },
  errore: { label: "Errore", cls: "text-red-600 bg-red-500/10 border-red-400/30" },
};

export const PortaliPanel = ({ id, im, isSegreteria, onChanged }) => {
  const [portals, setPortals] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/portali").then((r) => setPortals(r.data)).catch(() => setPortals([])); }, []);

  const ready = im.pub_mancanti ? im.pub_mancanti.length === 0 : im.documenti_pronti;
  const stato = im.portali || {};

  const run = async (key, fn) => {
    setBusy(key);
    try { const { data } = await fn(); onChanged(data); }
    catch (e) {
      const d = e.response?.data?.detail;
      if (d?.mancanti && d?.message?.startsWith("Pubblicazione bloccata")) toast.error(d.message);
      else if (d?.message === "PUBBLICAZIONE BLOCCATA") toast.error("Pubblicazione bloccata: documenti obbligatori non ancora approvati.");
      else if (d?.message === "DATI ESSENZIALI MANCANTI") toast.error(`Dati essenziali mancanti: ${d.mancanti.join(", ")}`);
      else toast.error(apiError(e));
    } finally { setBusy(""); }
  };

  const publish = () => {
    const chosen = Object.keys(sel).filter((k) => sel[k]);
    if (!chosen.length) return toast.error("Seleziona almeno un portale.");
    run("publish", async () => {
      const r = await api.post(`/immobili/${id}/portali/pubblica`, { portali: chosen });
      toast.success("Richiesta di pubblicazione inviata ai portali selezionati."); setSel({});
      return r;
    });
  };
  const resync = (pid) => run(`re-${pid}`, async () => { const r = await api.post(`/immobili/${id}/portali/${pid}/risincronizza`); toast.success("Risincronizzazione richiesta."); return r; });
  const remove = (pid) => run(`rm-${pid}`, async () => { const r = await api.post(`/immobili/${id}/portali/${pid}/rimuovi`); toast.success("Rimozione richiesta."); return r; });

  if (!ready) {
    return (
      <div className="card-immo p-8 text-center" data-testid="portali-blocked">
        <Lock className="mx-auto text-red-400 mb-3" size={28} />
        <div className="font-semibold">Pubblicazione bloccata</div>
        <p className="text-sm text-[var(--muted)] mt-1">Completa i documenti obbligatori per pubblicare ({im.pub_completi ?? 0}/{im.pub_totali ?? 8} approvati dalla Segreteria).{(im.pub_mancanti || []).length > 0 && <> Manca: {im.pub_mancanti.join(", ")}.</>}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="portali-panel">
      <div className="card-immo p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <div className="font-semibold flex items-center gap-2"><Globe size={16} /> Pubblica sui portali</div>
          {isSegreteria && (
            <button data-testid="pubblica-portali-btn" onClick={publish} disabled={busy === "publish"}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FC82ED] text-black font-semibold text-sm glow-pink disabled:opacity-50">
              {busy === "publish" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Pubblica sui portali selezionati
            </button>
          )}
        </div>
        <p className="text-xs text-[var(--muted)] mb-4">Solo la Segreteria può pubblicare. I dati (descrizione, foto, prezzo, classe energetica, caratteristiche) vengono inviati tramite API o feed XML ufficiale del portale. I connettori non ancora configurati riportano lo stato "Errore: credenziali ufficiali mancanti".</p>

        <div className="space-y-2">
          {portals.map((p) => {
            const st = stato[p.id] || {};
            const badge = STATO[st.stato] || STATO.non_pubblicato;
            const active = st.stato && st.stato !== "non_pubblicato";
            return (
              <div key={p.id} data-testid={`portale-${p.id}`} className="flex items-start gap-3 p-3 rounded-xl border border-[var(--border)]">
                {isSegreteria && (
                  <input type="checkbox" data-testid={`portale-check-${p.id}`} checked={!!sel[p.id]}
                         onChange={(e) => setSel({ ...sel, [p.id]: e.target.checked })}
                         className="mt-1 accent-[#FC82ED] w-4 h-4" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{p.label}</span>
                    <span data-testid={`portale-stato-${p.id}`} className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.label}</span>
                    {!p.configured && <span className="text-[10px] uppercase font-semibold text-amber-600">Non configurato</span>}
                  </div>
                  {st.last_sync && <div className="text-[11px] text-[var(--muted)] mt-0.5">Ultima sincronizzazione: {fmtDate(st.last_sync)}</div>}
                  {st.messaggio && <div className={`text-[11px] mt-0.5 ${st.stato === "errore" ? "text-red-500" : "text-[var(--muted)]"}`}>{st.messaggio}</div>}
                  {st.annuncio_url && <a href={st.annuncio_url} target="_blank" rel="noreferrer" data-testid={`portale-link-${p.id}`} className="text-[11px] mt-0.5 inline-flex items-center gap-1 text-[#FC82ED] hover:underline"><ExternalLink size={11} /> Vedi annuncio / feed</a>}
                </div>
                {isSegreteria && active && (
                  <div className="shrink-0 flex items-center gap-1.5">
                    <button data-testid={`portale-resync-${p.id}`} onClick={() => resync(p.id)} disabled={busy === `re-${p.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-600 hover:text-[#FC82ED] hover:border-[#FC82ED]/40 disabled:opacity-50">
                      {busy === `re-${p.id}` ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Modifica e risincronizza
                    </button>
                    <button data-testid={`portale-remove-${p.id}`} onClick={() => remove(p.id)} disabled={busy === `rm-${p.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50">
                      <Trash2 size={13} /> Rimuovi
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
